// DEPENDENCIES ---------------------------------------------------------------- //

import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { Moves } from '@gql/base-types';
import { useSubscribeRidehailsForMovesSubscription } from '../queries/subscribeRidehailsForMoves.subscription.generated';
import { isMoveLite } from '@utils/tiers';

import { css } from '@emotion/css';
import { Theme, useTheme, Paper, Typography, Chip, Button } from '@mui/material';
import { BiCar, BiCheckCircle, BiXCircle, BiTime, BiChevronRight } from 'react-icons/bi';
import  uberLogo from '@assets/images/uber.svg';
import  lyftLogo from '@assets/images/lyft.svg';

import InfoItem from '@components/InfoItem';
import RideProgressTracker from '@components/RideProgressTracker';
import { RidehailActions } from '@hopdrive/feature-ridehails';
import { DealerRidehailsHost } from '@features/ridehails/host';
import { ridesharePath } from '@features/products/productRoutes';


// HELPERS ---------------------------------------------------------------- //

const getProviderChip = (provider: string) => {
  const lowerProvider = provider?.toLowerCase() || '';

  if (lowerProvider.includes('uber')) {
    return (
      <Chip
        variant='filled'
        size='small'
        sx={{
          backgroundColor: '#000000',
          '& .MuiChip-label': {
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center'
          },
          '& img': {
            filter: 'invert(1) drop-shadow(0 0 0.5px white) drop-shadow(0 0 0.5px white)',
          }
        }}
        label={<img src={uberLogo} alt="Uber" style={{ width: 20, height: 20 }} />}
      />
    );
  }

  if (lowerProvider.includes('lyft')) {
    return (
      <Chip
        variant='filled'
        size='small'
        sx={{
          backgroundColor: '#FF00BF',
          '& .MuiChip-label': {
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center'
          },
          '& img': {
            filter: 'invert(1)',
          }
        }}
        label={<img src={lyftLogo} alt="Lyft" style={{ width: 20, height: 20 }} />}
      />
    );
  }

  return null;
};

const capitalizeWords = (str: string | null | undefined) => {
  if (!str) return str;
  return str.replace(/[_-]/g, ' ').split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

const getRideStatusColor = (status?: string | null, cancelStatus?: string | null) => {
  if (!status) return 'default';

  if (cancelStatus) return 'error';

  const lowerStatus = status.toLowerCase();

  if (lowerStatus === 'completed' || lowerStatus === 'delivery successful') return 'success';
  if (lowerStatus === 'in_progress' || lowerStatus === 'active' ||
      lowerStatus === 'driver_assigned' || lowerStatus === 'driver_arrived') return 'warning';
  if (lowerStatus === 'booked' || lowerStatus === 'booking' || lowerStatus === 'pending') return 'info';
  if (lowerStatus === 'canceled' || lowerStatus === 'cancelled' || lowerStatus === 'failed') return 'error';

  return 'default';
};

const getRideStatusIcon = (status?: string | null, cancelStatus?: string | null) => {
  if (!status) return <BiTime />;

  if (cancelStatus) return <BiXCircle />;

  const lowerStatus = status.toLowerCase();

  if (lowerStatus === 'completed' || lowerStatus === 'delivery successful') return <BiCheckCircle />;
  if (lowerStatus === 'canceled' || lowerStatus === 'cancelled' || lowerStatus === 'failed') return <BiXCircle />;

  return <BiTime />;
};

const getDisplayStatus = (ridehail?: any, move?: any) => {
  if (move?.cancel_status) return `Canceled: ${move?.cancel_reason || 'Unknown'}`;
  if (ridehail?.status) return capitalizeWords(ridehail.status);
  if (move?.status) return capitalizeWords(move.status);
  return 'Not Booked';
};

// TYPES ---------------------------------------------------------------- //

interface MoveDetailsRideProps {
  move?: Moves;
  isPrinting?: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const MoveDetailsRide = ({ move, isPrinting }: MoveDetailsRideProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const cls = useStyles(theme);

  // Get active ride moves from childMoves (filter out inactive/soft-deleted
  // rides). Lite scope comes from the ride OR its parent: backend-dispatched
  // rides carry lite=false even when the parent drive is lite, so requiring
  // ride.lite hid them entirely.
  const allActiveLiteRides = move?.childMoves?.filter(childMove =>
    childMove?.move_type === 'ride' && childMove?.active && (isMoveLite(childMove) || isMoveLite(move))
  ) || [];

  // Get move IDs for ridehails subscription
  const moveIds = allActiveLiteRides.map(rideMove => rideMove?.id).filter(Boolean);

  // Subscribe to ridehails data for real-time updates
  const { data: ridehailsData } = useSubscribeRidehailsForMovesSubscription({
    variables: { move_ids: moveIds },
    skip: !moveIds.length,
  });

  // Create a map of move_id to ridehail for easy lookup
  const ridehailsByMoveId = useMemo(() => {
    return ridehailsData?.ridehails?.reduce((acc: any, ridehail: any) => {
      if (!acc[ridehail.move_id] || new Date(ridehail.created_at) > new Date(acc[ridehail.move_id].created_at)) {
        acc[ridehail.move_id] = ridehail;
      }
      return acc;
    }, {}) || {};
  }, [ridehailsData]);

  if (!allActiveLiteRides.length) return null;

  return (
    <div className={cls.rideContainer}>
      {allActiveLiteRides.map((rideMove, index) => {
        const ridehail = ridehailsByMoveId[rideMove?.id || 0];
        const displayStatus = getDisplayStatus(ridehail, rideMove);
        const statusColor = getRideStatusColor(ridehail?.status || rideMove?.status, rideMove?.cancel_status);

        return (
          <Paper
            key={`ride-${rideMove?.id || index}`}
            variant={isPrinting ? 'customPrint' : 'custom'}
            sx={{ padding: '16px', marginBottom: index < allActiveLiteRides.length - 1 ? '12px' : 0 }}
          >
            <div className={cls.rideHeader}>
              <div className={cls.rideTitle}>
                <BiCar size={20} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Ride #{rideMove?.id}
                </Typography>
                {ridehail?.provider && (
                  <div style={{ marginLeft: '8px' }}>
                    {getProviderChip(ridehail.provider)}
                  </div>
                )}
              </div>

              <div className={cls.rideStatus}>
                <Chip
                  icon={getRideStatusIcon(ridehail?.status || rideMove?.status, rideMove?.cancel_status)}
                  label={displayStatus}
                  color={statusColor as any}
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<BiChevronRight />}
                  onClick={() => navigate(ridesharePath(rideMove?.id))}
                  sx={{ ml: 1 }}
                >
                  View Details
                </Button>
                {!isPrinting && move?.driver_id && (
                  <div style={{ marginLeft: '8px' }}>
                    {/* The shared actions read auth/config from the ridehails host context. */}
                    <DealerRidehailsHost>
                    <RidehailActions
                      move={{
                        id: rideMove?.id || 0,
                        pickup_time: rideMove?.pickup_time || undefined,
                        driver_status: rideMove?.driver_status || undefined,
                        customer: move?.customer as any,
                        lane: (rideMove?.lane || move?.lane) as any,
                        parentMove: { driver_status: move?.driver_status } as any,
                      }}
                      ridehail={ridehail}
                      onRefetch={() => {}} // No-op since subscription handles updates
                    />
                    </DealerRidehailsHost>
                  </div>
                )}
              </div>
            </div>

          <RideProgressTracker rideMove={rideMove} ridehailStatus={ridehail?.status} size="sm" />

          <div className={cls.rideInfo}>
            <div className={cls.rideInfoColumn}>
              <InfoItem
                keyText="Pickup Time"
                valueText={rideMove?.pickup_time
                  ? dayjs(rideMove.pickup_time).format('M/D/YYYY h:mm A')
                  : 'Not scheduled'
                }
              />
            </div>

            {rideMove?.consumer_name && (
              <div className={cls.rideInfoColumn}>
                <InfoItem
                  keyText="Rider"
                  valueText={rideMove.consumer_name}
                />
              </div>
            )}
          </div>
        </Paper>
      );
      })}
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  rideContainer: css`
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,

  rideHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  `,

  rideTitle: css`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${theme?.palette?.text?.primary};
  `,

  rideStatus: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,

  rideInfo: css`
    display: flex;
    gap: 20px;
    ${theme?.breakpoints?.down('md')} {
      flex-direction: column;
      gap: 6px;
    }
  `,

  rideInfoColumn: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default MoveDetailsRide;